<template>
  <section class="hnc">
    <div class="hnc-inner">
      <slot name="breadcrumb" />
      <div class="hnc-stage">
        <div class="hnc-stage-content">
          <h1
            v-if="hncTitle"
            class="hnc-title"
          >
            {{ hncTitle }}
          </h1>
          <div class="hnc-stage-meta">
            <h2
              v-if="hncSubtitle"
              class="hnc-subtitle"
            >
              {{ hncSubtitle }}
            </h2>

            <p
              v-if="hncParagraph"
              class="hnc-paragraph"
            >
              {{ hncParagraph }}
            </p>
            <p
              v-if="hncAddress"
              class="hnc-address"
            >
              {{ hncAddress }}
            </p>
            <p
              v-if="hncOtherCareTypePageLinks && hncOtherCareTypePageLinks.length"
              class="hnc-other-locations"
            >
              <span>{{ i18n.hnc.meta.label }}</span>
            </p>
            <div
              v-if="hncOtherCareTypePageLinks && hncOtherCareTypePageLinks.length"
              class="flex justify-center md:justify-start"
            >
              <div class="inline-flex gap-2 bg-white rounded-full p-2 mb-4 shadow-xs justify-center md:justify-start">
                <a
                  v-for="(pageLink, index) in hncOtherCareTypePageLinks"
                  :key="index"
                  :href="pageLink.href"
                  class="hnc-other-locations-link"
                  :class="{ 'is-active': pageLink.active }"
                >{{ pageLink.label }}</a>
              </div>
            </div>
          </div>

          <slot name="locator" />
          <div class="hnc-stage-image">
            <div class="hnc-stage-image-wrapper">
              <ShieldMaskFullRotated />
              <kf-image
                :src="headerContentImage"
                :alt="hncImageAlt"
              />
              <div class="hnc-curve hnc-stage-curve">
                <kf-curve
                  curve-alignment="left"
                  curve-svg-color="gray"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-12 px-2 md:px-0">
        <div class="col-span-12 md:col-span-7">
          <div class="block md:hidden short-intro-text">
            <slot name="short-intro-text" />
          </div>
        </div>
      </div>
      <div class="hnc-stage-bottom">
        <div class="grid grid-cols-12 px-2 md:px-0">
          <div class="col-span-12 lg:col-span-8">
            <div class="hnc-stage-bottom-left">
              <div class="hidden md:block">
                <slot name="short-intro-text" />
              </div>
              <slot name="stage-bottom-left" />
            </div>
          </div>
          <div class="col-span-12 lg:col-span-4">
            <div class="hnc-stage-bottom-right">
              <slot name="stage-bottom-right" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="hnc-curve">
      <kf-curve
        curve-alignment="left"
        curve-svg-color="white"
      />
    </div>
  </section>
</template>

<script>
import KfCurve from '@/components/base/curve/Curve.vue';
import KfImage from '@/components/base/image/Image.vue';
import { setDayCareString, trackEvent } from '@/lib/gtm/trackEvent';
import TailwindMixin from '@/mixins/TailwindMixin';

export default {
  name: 'KfHeaderNestedContent',
  components: {
    KfImage,
    KfCurve,
    ShieldMaskFullRotated: () =>
      import(/* webpackChunkName: "main"*/ '@/components/svg/ShieldMaskFullRotated.vue'),
  },
  mixins: [TailwindMixin],
  props: {
    i18n: {
      type: Object,
      default: () => {},
    },
    hncImageSrcMobile: {
      type: String,
      default: '',
    },
    hncImageSrcLarge: {
      type: String,
      default: '',
    },
    hncImageAlt: {
      type: String,
      default: '',
    },
    hncTitle: {
      type: String,
      default: '',
    },
    hncSubtitle: {
      type: String,
      default: '',
    },
    hncParagraph: {
      type: String,
      default: '',
    },
    hncAddress: {
      type: String,
      default: '',
    },
    hncOtherCareTypePageLinks: {
      type: Array,
      default: () => [],
    },
    event: {
      type: Object,
      default: null,
    },
  },
  computed: {
    headerContentImage() {
      if (this.doesWindowHaveMinWidthXs) {
        return this.hncImageSrcLarge;
      }
      return this.hncImageSrcMobile;
    },
    media() {
      return this.tailwindMixin.currentWindowWidth;
    },
  },
  created() {
    if (this.event) {
      // On Created/Mounted gtm is not enabled giving it a timeout of 0 fixes this O_o
      setTimeout(() => {
        trackEvent(
          { ...this.event, childcare_type: setDayCareString(this.event.childcare_type) },
          this.$gtm.enabled(),
        );
      }, 0);
    }
  },
  methods: {},
};
</script>

<style lang="postcss">
.hnc {
  @apply w-full flex flex-col justify-start relative bg-gray-50 overflow-hidden pb-12;
  min-height: 500px;

  @screen md {
    @apply pt-0;
    min-height: 670px;
  }

  .short-intro-text > * {
    @apply text-base text-center mb-8;

    @screen md {
      @apply text-black text-left;
    }
  }

  // curve color
  &.has-color-gray {
    .hnc-curve {
      & > svg {
        @apply text-primary-delta;
      }
    }
  }

  &-inner {
    @apply flex flex-col relative mx-auto z-20 w-full;

    @screen md {
      @apply px-4 container pb-10;
      min-width: 740px;
    }
  }

  &-stage {
    @apply grid grid-cols-12;
    padding: 97.3vw 30px 40px 30px;

    .hnc-stage-content {
      @apply absolute break-words p-7 w-full left-0 top-0 flex justify-center flex-col items-center z-10 text-white mb-3 order-2 bg-primary-alpha col-span-12 md:col-span-7;
      height: 97.3vw;

      &::before {
        @apply block;
        content: '';
        background-position: center bottom;
        background-repeat: no-repeat;
        padding-bottom: 9.7222222222vw;
        background-size: 100%;
        position: absolute;
        bottom: -1px;
        left: 0;
        background-image: url('~@/assets/images/curve-news-gray-alternative.svg?inline');
        width: calc(100% + 2px);
        z-index: 999;

        @screen md {
          @apply hidden;
        }
      }

      @screen md {
        @apply static p-0 w-full left-auto top-auto block text-black text-4xl h-auto bg-transparent;
      }
    }

    &-image {
      @screen md {
        @apply absolute flex items-center justify-center top-0;
        width: 440px;
        height: 440px;
        left: 60%;
        transform: translateY(-45px) translateZ(0);
      }

      @screen lg {
        transform: translateY(-45px) translateZ(0);
      }

      @screen xl {
        width: 570px;
        height: 570px;
        transform: translateY(-100px) translateZ(0);
      }

      @screen 2xl {
        width: 790px;
        height: 790px;
        transform: translateY(-150px) translateZ(0);
      }

      &-wrapper {
        @apply absolute left-0 top-0 h-full w-full z-10 overflow-hidden bg-black;

        @screen md {
          @apply relative;
          clip-path: url(#shape-blob-rotated);
          height: auto;
        }

        @screen lg {
          @apply relative h-full w-full;
        }

        img {
          @apply opacity-70 object-cover w-full object-center;
          height: 100%;

          @screen md {
            @apply opacity-100 h-full;
          }
        }
      }
    }

    @screen md {
      @apply p-0;

      .hnc-stage-content,
      .hnc-stage-image {
        @apply order-none;
      }
    }

    .hnc-stage-curve {
      @apply hidden;

      @screen md {
        @apply hidden;
      }
    }

    &-bottom {
      &-left {
        @apply relative bg-white rounded-smd p-7 z-10 mb-8;
        box-shadow: 0 4px 20px rgb(54, 25, 5, 0.12);

        @screen md {
          @apply mb-0;
        }

        @screen lg {
          @apply mb-0 p-16;
        }

        @screen xl {
          @apply py-14 px-28;
        }

        > div,
        > section {
          @apply mb-8;

          &:last-child {
            @apply mb-0;
          }
        }
      }

      &-right {
        padding: 0;

        @screen md {
          padding: 240px 0 0 60px;
        }

        @screen xl {
          padding-top: 355px;
        }

        @screen 2xl {
          padding: 450px 0 0 120px;
        }

        .richtext {
          @apply mb-8;

          p,
          a {
            @apply text-base mb-0;
          }
        }
      }
    }

    &-meta {
      @apply relative z-20;
    }
  }

  &-title {
    @apply relative z-20 text-white;
    text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);

    @screen md {
      @apply static p-0 w-full left-auto top-auto block text-black text-4xl h-auto;
      text-shadow: none;
    }

    @screen lg {
      @apply text-5xl;
    }
  }

  &-subtitle {
    @apply mb-2 pt-4 text-xl italic text-white text-center;
    text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);

    @screen md {
      @apply pt-0 text-black text-left;
      text-shadow: none;
    }
  }

  &-paragraph {
    @apply mb-8 text-lg text-white text-center;

    @screen md {
      @apply pt-0 text-black text-left;
    }
  }

  &-address {
    @apply mb-8 text-lg italic text-white text-center;
    text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);

    @screen md {
      @apply pt-0 text-black text-left;
      text-shadow: none;
    }
  }

  &-other-locations {
    @apply mb-2 text-sm text-white text-center;
    text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);

    @screen md {
      @apply pt-0 text-black text-left;
      text-shadow: none;
    }
  }

  &-other-locations-link {
    @apply flex h-10 items-center bg-transparent text-primary-bravo justify-center text-sm font-medium px-5 rounded no-underline;
    transition: color 0.2s, background 0.2s, box-shadow 0.2s;

    &:hover {
      @apply text-secondary-bravo shadow-sm;
    }

    &.is-active {
      @apply text-primary-bravo text-white  bg-secondary-bravo;
      pointer-events: none;
    }
  }

  &-link {
    @apply mb-8 text-white;
    &:last-of-type {
      @apply mb-0;
    }
  }

  &-curve {
    @apply absolute left-0 bottom-0 w-full overflow-hidden z-10;

    & > svg {
      @apply absolute top-0 left-0 w-full h-full text-white;
    }
    /*
    @screen md {
      @apply block;
    } */
  }
}

.venue-contract-title {
  @apply text-4xl;
}

.venue-contact-block {
  @apply mb-6;

  a {
    @apply text-primary-bravo no-underline;

    &:hover {
      @apply underline;
    }
  }
}
</style>
